import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet, Pressable, ScrollView } from 'react-native-web';
import Card from '../components/Card';
import Button from '../components/Button';
import Badge from '../components/Badge';
import Modal from '../components/Modal';
import PageHeader from '../components/PageHeader';
import { colors, radius } from '../theme';
import { getScripts, getEventLogs } from '../api';
import { formatDistanceToNow } from 'date-fns';

export default function LogsPage() {
  const [logs, setLogs] = useState({ content: [], totalElements: 0, totalPages: 0 });
  const [scripts, setScripts] = useState([]);
  const [selectedScript, setSelectedScript] = useState('');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [detailModal, setDetailModal] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [logsData, scriptsData] = await Promise.all([
        getEventLogs(selectedScript || null, page, 50),
        getScripts(),
      ]);
      setLogs(logsData);
      setScripts(scriptsData);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [selectedScript, page]);

  useEffect(() => { load(); }, [load]);

  const formatTime = (ts) => {
    try { return formatDistanceToNow(new Date(ts), { addSuffix: true }); }
    catch { return ts; }
  };

  const statusColor = (s) => {
    if (s === 'sent') return 'success';
    if (s === 'failed') return 'danger';
    if (s === 'skipped') return 'muted';
    return 'warning';
  };

  return (
    <View>
      <PageHeader title="Event Logs" subtitle={`${(logs.totalElements || 0).toLocaleString()} events`}>
        <Button label="Refresh" icon="🔄" variant="ghost" onPress={load} />
      </PageHeader>

      {/* Filter */}
      <Card style={styles.filterCard}>
        <View style={styles.filterRow}>
          <Text style={styles.filterLabel}>Script:</Text>
          <select
            value={selectedScript}
            onChange={(e) => { setSelectedScript(e.target.value); setPage(0); }}
            style={selectStyle}
          >
            <option value="">All Scripts</option>
            {scripts.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </View>
      </Card>

      {/* Table */}
      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <View style={styles.tableHeader}>
          <Text style={[styles.th, { width: 120 }]}>Time</Text>
          <Text style={[styles.th, { width: 100 }]}>Origin</Text>
          <Text style={[styles.th, { width: 90 }]}>Action</Text>
          <Text style={[styles.th, { width: 130 }]}>Event</Text>
          <Text style={[styles.th, { width: 90 }]}>IP</Text>
          <Text style={[styles.th, { width: 110 }]}>→ Pixel</Text>
          <Text style={[styles.th, { width: 80 }]}>Status</Text>
          <Text style={[styles.th, { width: 40 }]}></Text>
        </View>

        <ScrollView style={{ maxHeight: '60vh' }}>
          {(logs.content || []).map((log) => (
            <Pressable
              key={log.id}
              style={({ hovered }) => [styles.tableRow, hovered && styles.tableRowHover]}
              onPress={() => setDetailModal(log)}
            >
              <Text style={[styles.td, { width: 120 }]} numberOfLines={1}>
                {formatTime(log.createdAt)}
              </Text>
              <Text style={[styles.td, styles.mono, { width: 100 }]} numberOfLines={1}>
                {log.originDomain || '—'}
              </Text>
              <View style={{ width: 90, paddingHorizontal: 6 }}>
                <Badge label={log.action} color={log.action === 'track' ? 'success' : log.action === 'init' ? 'warning' : 'primary'} />
              </View>
              <Text style={[styles.td, styles.eventName, { width: 130 }]} numberOfLines={1}>
                {log.eventName || '—'}
              </Text>
              <Text style={[styles.td, styles.mono, { width: 90, fontSize: 11 }]} numberOfLines={1}>
                {log.clientIpAddress || '—'}
              </Text>
              <Text style={[styles.td, styles.mono, { width: 110, fontSize: 11 }]} numberOfLines={1}>
                {log.fbPixelId || '—'}
              </Text>
              <View style={{ width: 80, paddingHorizontal: 6 }}>
                <Badge label={log.forwardStatus} color={statusColor(log.forwardStatus)} />
              </View>
              <Text style={[styles.td, { width: 40, textAlign: 'center' }]}>→</Text>
            </Pressable>
          ))}

          {(logs.content || []).length === 0 && !loading && (
            <View style={styles.emptyRow}>
              <Text style={styles.emptyText}>No events found</Text>
            </View>
          )}
        </ScrollView>

        {/* Pagination */}
        {logs.totalPages > 1 && (
          <View style={styles.pagination}>
            <Button label="← Prev" variant="ghost" size="sm" onPress={() => setPage(Math.max(0, page - 1))} />
            <Text style={styles.pageInfo}>Page {page + 1} / {logs.totalPages}</Text>
            <Button label="Next →" variant="ghost" size="sm" onPress={() => setPage(Math.min(logs.totalPages - 1, page + 1))} />
          </View>
        )}
      </Card>

      {/* Detail Modal */}
      <Modal visible={!!detailModal} title="Event Details" onClose={() => setDetailModal(null)}>
        {detailModal && (
          <View>
            <SectionTitle>Source</SectionTitle>
            <DetailRow label="Script" value={detailModal.scriptName} />
            <DetailRow label="Origin Domain" value={detailModal.originDomain || '—'} />
            <DetailRow label="URL" value={detailModal.eventSourceUrl || '—'} />
            <DetailRow label="IP" value={detailModal.clientIpAddress || '—'} />

            <SectionTitle>Event (from fbq)</SectionTitle>
            <DetailRow label="Action" value={detailModal.action} />
            <DetailRow label="Event Name" value={detailModal.eventName || '—'} />
            <DetailRow label="FBP" value={detailModal.fbp || '—'} />
            <DetailRow label="FBC" value={detailModal.fbc || '—'} />
            <DetailRow label="Event Time" value={new Date(detailModal.eventTime * 1000).toISOString()} />

            {detailModal.customData && (
              <View style={styles.codeSection}>
                <Text style={styles.codeSectionTitle}>Custom Data</Text>
                <View style={styles.codeBlock}>
                  <Text style={styles.codeText}>{JSON.stringify(detailModal.customData, null, 2)}</Text>
                </View>
              </View>
            )}

            {detailModal.rawArgs && (
              <View style={styles.codeSection}>
                <Text style={styles.codeSectionTitle}>Raw fbq() Arguments</Text>
                <View style={styles.codeBlock}>
                  <Text style={styles.codeText}>{JSON.stringify(detailModal.rawArgs, null, 2)}</Text>
                </View>
              </View>
            )}

            <SectionTitle>Forward Result</SectionTitle>
            <DetailRow label="→ Pixel ID" value={detailModal.fbPixelId || '—'} />
            <DetailRow label="Status" value={detailModal.forwardStatus} />
            <DetailRow label="Forwarded At" value={detailModal.forwardedAt || '—'} />

            {detailModal.forwardResponse && (
              <View style={styles.codeSection}>
                <Text style={styles.codeSectionTitle}>FB API Response</Text>
                <View style={styles.codeBlock}>
                  <Text style={styles.codeText}>{detailModal.forwardResponse}</Text>
                </View>
              </View>
            )}
          </View>
        )}
      </Modal>
    </View>
  );
}

function SectionTitle({ children }) {
  return <Text style={sectionStyles.title}>{children}</Text>;
}

function DetailRow({ label, value }) {
  return (
    <View style={detailStyles.row}>
      <Text style={detailStyles.label}>{label}</Text>
      <Text style={detailStyles.value} selectable>{typeof value === 'string' ? value : JSON.stringify(value)}</Text>
    </View>
  );
}

const sectionStyles = StyleSheet.create({
  title: {
    fontSize: 13, fontWeight: '700', color: colors.primary, marginTop: 20, marginBottom: 8,
    textTransform: 'uppercase', letterSpacing: 0.5,
    paddingTop: 12, borderTopWidth: 1, borderTopColor: colors.border,
  },
});

const detailStyles = StyleSheet.create({
  row: { flexDirection: 'row', paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.03)' },
  label: { width: 120, fontSize: 12, fontWeight: '600', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 },
  value: { flex: 1, fontSize: 13, color: colors.text, fontFamily: 'monospace' },
});

const selectStyle = {
  backgroundColor: '#0f111a', color: '#e2e8f0', border: '1px solid #2a2e42',
  borderRadius: 8, padding: '8px 14px', fontSize: 14, width: 250, outline: 'none',
};

const styles = StyleSheet.create({
  filterCard: { marginBottom: 16 },
  filterRow: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  filterLabel: { fontSize: 13, fontWeight: '600', color: colors.textSecondary },
  tableHeader: {
    flexDirection: 'row', alignItems: 'center', paddingVertical: 12, paddingHorizontal: 16,
    backgroundColor: 'rgba(255,255,255,0.03)', borderBottomWidth: 1, borderBottomColor: colors.border,
  },
  th: { fontSize: 11, fontWeight: '700', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, paddingHorizontal: 6 },
  tableRow: {
    flexDirection: 'row', alignItems: 'center', paddingVertical: 10, paddingHorizontal: 16,
    borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.03)', cursor: 'pointer',
  },
  tableRowHover: { backgroundColor: 'rgba(99,102,241,0.05)' },
  td: { fontSize: 13, color: colors.text, paddingHorizontal: 6 },
  eventName: { fontWeight: '600', color: colors.primaryHover },
  mono: { fontFamily: 'monospace' },
  emptyRow: { paddingVertical: 40, alignItems: 'center' },
  emptyText: { color: colors.textMuted, fontSize: 14 },
  pagination: {
    flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: 16,
    paddingVertical: 12, borderTopWidth: 1, borderTopColor: colors.border,
  },
  pageInfo: { fontSize: 13, color: colors.textSecondary },
  codeSection: { marginTop: 12 },
  codeSectionTitle: { fontSize: 12, fontWeight: '700', color: colors.textSecondary, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 },
  codeBlock: { backgroundColor: colors.bg, borderRadius: radius.sm, padding: 16, borderWidth: 1, borderColor: colors.border },
  codeText: { fontFamily: 'monospace', fontSize: 12, color: colors.success, whiteSpace: 'pre-wrap', userSelect: 'all' },
});
